"use client";

import React from "react";
import { Images } from "@/app/assets/shared";
import Image from "next/image";
import { useIsMobile } from "hooks/useMediaQuery";

interface CtaNewProps {
  bg?: string;
  bgImage?: any;
  title1?: React.ReactNode;
  title2?: React.ReactNode;
  description?: React.ReactNode;
  cardBg?: string;
  borderGlowColor?: string;
}

export default function CTANEW({
  bg = "bg-[#0b41b3]",
  bgImage = Images?.serviceCtaBg,
  title1,
  title2,
  description,
  cardBg = "bg-white/10 backdrop-blur-md",
  borderGlowColor = "255,255,255",
}: CtaNewProps) {
  return (
    <section className={`cta-new relative overflow-hidden ${bg}`}>
      {/* Layered mesh-gradient blobs for a dynamic, non-flat background */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="cta-blob cta-blob-1" />
        <div className="cta-blob cta-blob-2" />
        <div className="cta-blob cta-blob-3" />
      </div>

      {/* Subtle grid pattern overlay for texture */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none opacity-[0.09]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
        aria-hidden="true"
      />

      {/* Original background image, layered above blobs/grid, blended */}
      {bgImage && (
        <div className="absolute inset-0 z-[1] pointer-events-none">
          <Image
            src={bgImage?.src || bgImage}
            alt="texture"
            fill
            className="object-cover mix-blend-overlay opacity-30"
            sizes="100vw"
          />
        </div>
      )}

      {/* Vignette to focus attention toward the card */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(5,15,50,0.45) 100%)",
        }}
        aria-hidden="true"
      />

      <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)] py-[60px] xl:py-[120px] relative z-[3]">
        {/* Main Frosted Glass Card Container */}
        <div
          className={`cta-card cta-border-pulse relative rounded-[24px] overflow-hidden text-center flex flex-col border ${cardBg}`}
          style={{ "--glow-rgb": borderGlowColor } as React.CSSProperties}
        >
          <div
            className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none z-[3]"
            aria-hidden="true"
          />

          <div className="relative z-[2] w-full py-10 lg:py-16 px-6 lg:px-12 bg-white/[0.06] flex flex-col items-center justify-center">
            <h2
              className="leading-[110%] text-[clamp(2rem,4vw,4.5rem)] text-white"
              style={{ textShadow: "0 2px 12px rgba(0,0,0,0.25)" }}
            >
              <span className="font-medium clash-font capitalize leading-[100%]">
                {title1}
              </span>{" "}
              <span className="font-medium clash-font capitalize leading-[100%]">
                {title2}
              </span>
            </h2>
          </div>

          <div
            className="relative z-[2] w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"
            aria-hidden="true"
          />

          {description && (
            <div className="relative z-[2] w-full py-8 lg:py-12 px-6 lg:px-12 flex flex-col items-center justify-center">
              <p className="w-full text-center text-white/80 text-[length:var(--hero-para)] font-normal leading-[160%]">
                {description}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
